import { Fragment, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { ScrollText, Loader2, Search, RefreshCw, AlertTriangle, ChevronRight, Image as ImageIcon } from "lucide-react";

const eur = (n) => (n == null || n === "" ? "—" : Number(n).toLocaleString("it-IT", { style: "currency", currency: "EUR" }));
const numFmt = (n) => (n == null || n === "" ? "—" : Number(n).toLocaleString("it-IT", { maximumFractionDigits: 3 }));
const perKwp = (price, kwp) => (price != null && price !== "" && kwp) ? `(${Math.round(Number(price) / Number(kwp)).toLocaleString("it-IT")} €/kWp)` : null;
const dimStr = (d) => {
  if (!d) return null;
  if (typeof d === "object") { const l = d.larghezza, h = d.lunghezza; return (l && h) ? `${l}×${h} mm` : null; }
  return `${d} mm`;
};

export default function Listino() {
  const [items, setItems] = useState([]);
  const [available, setAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const toggle = (k) => setExpanded((e) => ({ ...e, [k]: !e[k] }));

  const load = () => {
    setLoading(true);
    setError(null);
    api.get("/listino")
      .then((r) => { setItems(r.data.items || []); setAvailable(r.data.available !== false); })
      .catch((e) => setError(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((it) =>
      [it.codice, it.descrizione, it.tipologia, it.marca].some((v) => String(v || "").toLowerCase().includes(s))
    );
  }, [items, q]);

  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach((it) => {
      const key = (it.tipologia || "").trim() || "Altro";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(it);
    });
    return Array.from(map.entries());
  }, [filtered]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-testid="listino-page">
      <div className="flex items-start justify-between gap-4 mb-1">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-primary/10 p-2"><ScrollText className="h-5 w-5 text-primary" strokeWidth={1.5} /></div>
          <div>
            <h1 className="text-2xl font-bold leading-tight">Listino ufficiale</h1>
            <p className="text-sm text-muted-foreground">Fonte unica: <b>rete.torri.solar</b> · sola lettura</p>
          </div>
        </div>
        <button onClick={load} disabled={loading} className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm hover:bg-secondary/50 transition-colors disabled:opacity-50" data-testid="listino-refresh-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Aggiorna
        </button>
      </div>

      <div className="relative mt-5 mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca codice, descrizione, marca…" className="pl-9" data-testid="listino-search" />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-16 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Caricamento listino…</div>
      ) : error ? (
        <Card className="p-6 border-destructive/40 bg-destructive/5 flex items-start gap-3" data-testid="listino-error">
          <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
          <div><div className="font-semibold text-destructive">Errore nel caricamento</div><div className="text-sm text-muted-foreground mt-0.5">{error}</div></div>
        </Card>
      ) : !available ? (
        <Card className="p-6 border-amber-400/50 bg-amber-50/60 dark:bg-amber-950/20 flex items-start gap-3" data-testid="listino-unavailable">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-amber-800 dark:text-amber-300">Listino non disponibile</div>
            <div className="text-sm text-muted-foreground mt-0.5">L'endpoint <code>GET /api/public/listino</code> di rete.torri.solar non ha risposto. Riprova più tardi.</div>
          </div>
        </Card>
      ) : (
        <Card className="overflow-hidden border-border" data-testid="listino-table-card">
          <div className="px-4 py-2.5 border-b border-border text-xs text-muted-foreground flex items-center justify-between">
            <span><b className="text-foreground">{filtered.length}</b> voci{q ? ` (di ${items.length})` : ""}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="listino-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
                  <th className="px-4 py-2.5 font-semibold w-16">Foto</th>
                  <th className="px-4 py-2.5 font-semibold">Codice</th>
                  <th className="px-4 py-2.5 font-semibold">Descrizione</th>
                  <th className="px-4 py-2.5 font-semibold">Tipologia</th>
                  <th className="px-4 py-2.5 font-semibold">Marca</th>
                  <th className="px-4 py-2.5 font-semibold">UM</th>
                  <th className="px-4 py-2.5 font-semibold text-right">kWp</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Prezzo listino</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Prezzo target</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">Nessuna voce trovata.</td></tr>
                ) : grouped.map(([cat, rows]) => (
                  <Fragment key={cat}>
                    <tr className="bg-secondary/40 border-y border-border">
                      <td colSpan={9} className="px-4 py-2 text-xs uppercase tracking-wide font-bold text-primary" data-testid={`listino-group-${cat}`}>{cat} <span className="text-muted-foreground font-normal normal-case">· {rows.length} voci</span></td>
                    </tr>
                    {rows.map((it, i) => {
                  const rk = `${cat}-${it.codice}-${i}`;
                  const open = !!expanded[rk];
                  const pkL = perKwp(it.prezzo_listino, it.kwp);
                  const pkT = perKwp(it.prezzo_target, it.kwp);
                  const specs = [
                    dimStr(it.dimensioni_mm) ? ["Dimensioni", dimStr(it.dimensioni_mm)] : null,
                    (it.potenza_wp != null && it.potenza_wp !== "") ? ["Potenza", `${it.potenza_wp} Wp`] : null,
                    (it.potenza_kw != null && it.potenza_kw !== "") ? ["Potenza", `${it.potenza_kw} kW`] : null,
                    (it.capacita_kwh != null && it.capacita_kwh !== "") ? ["Capacità", `${it.capacita_kwh} kWh`] : null,
                  ].filter(Boolean);
                  return (
                  <tr key={rk} className="border-b border-border/60 hover:bg-secondary/30 transition-colors align-top" data-testid={`listino-row-${it.codice || i}`}>
                    <td className="px-4 py-2.5">
                      {it.immagine_url ? (
                        <img src={it.immagine_url} alt={it.codice || ""} className="w-12 h-12 rounded object-cover border border-border bg-secondary/30" data-testid={`listino-img-${it.codice || i}`} />
                      ) : (
                        <div className="w-12 h-12 rounded border border-border bg-secondary/30 flex items-center justify-center" data-testid={`listino-img-placeholder-${it.codice || i}`}><ImageIcon className="h-5 w-5 text-muted-foreground/40" strokeWidth={1.5} /></div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs font-semibold">{it.codice || "—"}</td>
                    <td className="px-4 py-2.5 max-w-md">
                      {it.descrizione ? (
                        <div>
                          <button type="button" onClick={() => toggle(rk)} className="flex items-start gap-1.5 text-left w-full" data-testid={`listino-desc-toggle-${it.codice || i}`}>
                            <ChevronRight className={`h-4 w-4 mt-0.5 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-90" : ""}`} />
                            <span className={open ? "" : "line-clamp-1 text-muted-foreground"}>{it.descrizione}</span>
                          </button>
                          {open && specs.length > 0 && (
                            <div className="mt-2 ml-5 flex flex-wrap gap-2" data-testid={`listino-specs-${it.codice || i}`}>
                              {specs.map(([label, value], si) => (
                                <span key={si} className="inline-flex items-center gap-1 rounded-full border border-border bg-background/70 px-2 py-0.5 text-[11px]">
                                  <span className="text-muted-foreground">{label}:</span> <b>{value}</b>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-2.5">{it.tipologia ? <Badge variant="secondary" className="font-normal">{it.tipologia}</Badge> : "—"}</td>
                    <td className="px-4 py-2.5">{it.marca || "—"}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{it.um || "—"}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{numFmt(it.kwp)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-primary">
                      {eur(it.prezzo_listino)}
                      {pkL && <span className="block text-[10px] font-normal text-muted-foreground">{pkL}</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-semibold">
                      {eur(it.prezzo_target)}
                      {pkT && <span className="block text-[10px] font-normal text-muted-foreground">{pkT}</span>}
                    </td>
                  </tr>
                  );
                })}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
